import gsap from "gsap";

const getMobileScrollContainer = () => window.matchMedia("(max-width: 600px)").matches
  ? document.body
  : null;

const lenisScroll = (target) => {
  const element = document.getElementById(target);
  const header = Array.from(
    document.querySelectorAll(".bt-site-header"),
  ).find((candidate) => candidate.getBoundingClientRect().height > 0);
  if (!element) return;

  const headerRect = header ? header.getBoundingClientRect() : { height: 0 };

  setTimeout(() => {
    if (window.lenis) {
      window.lenis.scrollTo(element, {
        offset: -headerRect.height,
      });
      return;
    }

    const scrollContainer = getMobileScrollContainer();
    if (scrollContainer) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const targetTop = element.getBoundingClientRect().top - containerRect.top + scrollContainer.scrollTop - headerRect.height;
      scrollContainer.scrollTo({ top: Math.max(0, targetTop), behavior: "smooth" });
      return;
    }

    element.scrollIntoView({ behavior: "smooth", block: "start" });
  }, 150);
};

const smoothScrollWhenHash = () => {
  const hash = window.location.hash;

  if (hash) {
    const extract = hash.split("!");
    lenisScroll(extract[1] || extract[0].replace(/^#/, ""));
  }
};

const smoothScrollAnchors = () => {
  document.addEventListener("click", (event) => {
    const link = event.target.closest("a[href]");
    if (!link || event.defaultPrevented || event.button !== 0) return;

    const url = new URL(link.href, window.location.href);
    if (
      !url.hash ||
      url.origin !== window.location.origin ||
      url.pathname !== window.location.pathname ||
      link.target === "_blank" ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const target = document.getElementById(url.hash.slice(1));
    if (!target) return;

    event.preventDefault();
    history.pushState(null, "", url.hash);
    lenisScroll(target.id);
  });
};

const initAnchorActiveState = () => {
  const links = Array.from(
    document.querySelectorAll(
      '.bt-mockup-nav a[href^="#"], .bt-site-footer__columns a[href^="#"]',
    ),
  ).filter((link) => link.hash && document.getElementById(link.hash.slice(1)));
  if (!links.length) return;

  const sections = Array.from(
    new Set(links.map((link) => document.getElementById(link.hash.slice(1)))),
  );
  let ticking = false;

  const updateActive = () => {
    const marker = window.innerHeight * 0.28;
    let activeSection = null;

    sections.forEach((section) => {
      const rect = section.getBoundingClientRect();
      if (rect.top <= marker && rect.bottom > marker) activeSection = section;
    });

    if (
      !activeSection &&
      window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 4
    ) {
      activeSection = sections[sections.length - 1];
    }

    links.forEach((link) => {
      link.classList.toggle("is-active", link.hash.slice(1) === activeSection?.id);
    });
    ticking = false;
  };

  const queueUpdate = () => {
    if (ticking) return;
    window.requestAnimationFrame(updateActive);
    ticking = true;
  };

  const scrollContainer = getMobileScrollContainer();
  if (scrollContainer) scrollContainer.addEventListener("scroll", queueUpdate, { passive: true });
  else window.addEventListener("scroll", queueUpdate, { passive: true });
  if (window.lenis) window.lenis.on("scroll", queueUpdate);
  queueUpdate();
};

const hoverFloatingMenu = () => {
  if (isMobile) return;

  const floating = document.querySelector(".floating-menu-hove-state");
  const hasActivePreview = document.querySelector(".active-preview");

  const position = (rect) => {
    floating.style.width = `${rect.width}px`;
    floating.style.transform = `translate3d(${rect.x}px, ${rect.y}px, 0)`;
  };

  if (floating) {
    const menu = document.querySelectorAll(".main-header ul li a");
    if (!menu.length) return;

    const activeMenu = document.querySelector(
      ".main-header li.current_page_item a",
    );

    let rectFirst = menu[0].getBoundingClientRect();
    if (activeMenu) rectFirst = activeMenu.getBoundingClientRect();

    position(rectFirst); // Initial

    menu.forEach((item, index) => {
      const parent = item.parentNode;
      parent.addEventListener("mouseover", (e) => {
        const isComingSoon = parent.classList.contains("soon");

        floating.classList.add("active");
        const rect = item.getBoundingClientRect();
        position(rect); // Initial
        floating.style.opacity = 1;

        if (activeMenu)
          activeMenu.classList.add("disabled-state", "hover-active");

        if (isComingSoon) {
          e.preventDefault();

          parent.classList.add("active");

          if (!hasActivePreview) floating.classList.add("soon");
          else item.classList.add("in-preview");
        }
      });

      parent.addEventListener("mouseout", () => {
        menu.forEach((i) => {
          i.parentNode.classList.remove("active");
          i.classList.remove("in-preview");
        });
        floating.classList.remove("soon");

        if (activeMenu) {
          const rect = activeMenu.getBoundingClientRect();
          position(rect); // Initial

          activeMenu.classList.remove("hover-active");
        } else {
          floating.style.width = `${floating.height}px`;
          floating.classList.remove("active");
        }
      });
    });
  }
};

const burgerMenu = () => {
  const burger = document.getElementById("burger");
  const closeBurger = document.getElementById("close-burger");
  const mobileNavigation = document.querySelector(".mobile-navigation");
  if (!burger || !closeBurger || !mobileNavigation) return;
  const socialMedia = document.querySelector(
    ".mobile-navigation .social-media",
  );
  const mainMenu = document.querySelectorAll(
    ".mobile-navigation .menu-container li a",
  );
  let isMenuActive = false;
  const tl = gsap.timeline();

  burger.addEventListener("click", () => {
    if (!isMenuActive) {
      lenis.stop();
      burger.classList.add("hid");
      closeBurger.classList.add("show");

      tl.to(mobileNavigation, {
        x: 0,
        duration: 1,
        ease: "power4.inOut",
      }).to([mainMenu, socialMedia], {
        y: 0,
        opacity: 1,
        duration: 1,
        stagger: 0.01,
        ease: "power2.inOut",
      });

      isMenuActive = true;
    } else {
      lenis.start();
      burger.classList.remove("hid");

      closeBurger.classList.remove("show");

      gsap.to(mobileNavigation, {
        x: `-110%`,
        duration: 1,
        ease: "power4.inOut",
        onComplete: () => {
          gsap.set([mainMenu, socialMedia], {
            y: "100%",
            opacity: 0,
          });
        },
      });

      isMenuActive = false;
    }
  });
};

const initStickyHeader = () => {
  const headers = Array.from(
    document.querySelectorAll(".bt-site-header"),
  );
  if (!headers.length) return;

  const scrollContainer = getMobileScrollContainer();
  const getScrollY = () => scrollContainer ? scrollContainer.scrollTop : window.scrollY;
  let lastScrollY = getScrollY();
  let ticking = false;

  const updateHeader = (nextScrollY = getScrollY()) => {
    const currentScrollY = Math.max(nextScrollY, 0);
    const scrollDelta = currentScrollY - lastScrollY;

    if (currentScrollY <= 12) {
      headers.forEach((header) => header.classList.remove("is-hidden", "is-scrolling-up", "is-scrolled"));
    } else if (scrollDelta < -4) {
      headers.forEach((header) => {
        header.classList.remove("is-hidden");
        header.classList.add("is-scrolling-up", "is-scrolled");
      });
    } else if (scrollDelta > 4) {
      headers.forEach((header) => {
        header.classList.add("is-hidden", "is-scrolled");
        header.classList.remove("is-scrolling-up");
      });
    }

    lastScrollY = currentScrollY;
    ticking = false;
  };

  const queueUpdate = (nextScrollY) => {
    if (ticking) return;
    window.requestAnimationFrame(() => {
      updateHeader(nextScrollY);
      ticking = false;
    });
    ticking = true;
  };

  const handleNativeScroll = () => {
    const currentScrollTop = getScrollY();
    if (currentScrollTop <= 12) {
      updateHeader(0);
      return;
    }
    queueUpdate(currentScrollTop);
  };

  if (scrollContainer) scrollContainer.addEventListener("scroll", handleNativeScroll, { passive: true });
  window.addEventListener("scroll", handleNativeScroll, { passive: true });

  if (window.lenis) {
    window.lenis.on("scroll", (event) => {
      queueUpdate(event?.scroll ?? event?.animatedScroll ?? getScrollY());
    });
  }

  window.setInterval(() => {
    if (getScrollY() <= 12 && headers.some((header) => header.classList.contains("is-scrolled"))) {
      updateHeader(0);
    }
  }, 100);

  headers.forEach((header) => header.addEventListener("focusin", () => {
    header.classList.remove("is-hidden");
  }));
};

const initHomeMobileMenu = () => {
  const toggles = Array.from(document.querySelectorAll(".bt-mockup-menu"));
  const panels = Array.from(document.querySelectorAll(".bt-mobile-menu"));
  if (!toggles.length || !panels.length) return;

  const close = () => {
    document.body.classList.remove("bt-mobile-menu-open");
    toggles.forEach((toggle) => {
      toggle.setAttribute("aria-expanded", "false");
      toggle.setAttribute("aria-label", "Open menu");
    });
    panels.forEach((panel) => panel.setAttribute("aria-hidden", "true"));
    window.lenis?.start();
  };

  const toggleMenu = () => {
    const isOpen = document.body.classList.toggle("bt-mobile-menu-open");
    toggles.forEach((toggle) => {
      toggle.setAttribute("aria-expanded", String(isOpen));
      toggle.setAttribute("aria-label", isOpen ? "Close menu" : "Open menu");
    });
    panels.forEach((panel) => panel.setAttribute("aria-hidden", String(!isOpen)));
    if (isOpen) window.lenis?.stop(); else window.lenis?.start();
  };

  toggles.forEach((toggle) => toggle.addEventListener("click", toggleMenu));
  panels.forEach((panel) => {
    panel.querySelectorAll('a[href^="#"]').forEach((link) => link.addEventListener("click", close));
    panel.querySelectorAll('a[href]:not([href^="#"])').forEach((link) => link.addEventListener("click", close));
  });
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && document.body.classList.contains("bt-mobile-menu-open")) close();
  });
};

document.addEventListener("DOMContentLoaded", () => {
  // burger();
  burgerMenu();
  initHomeMobileMenu();
  initStickyHeader();
  smoothScrollAnchors();
  smoothScrollWhenHash();
  initAnchorActiveState();
  hoverFloatingMenu();
});

document.addEventListener("wpcf7mailsent", function (event) {
  const formPopUp = document.querySelector(".form-popup");
  if (formPopUp) {
    const form = formPopUp.querySelector("form");
    const outPut = form.querySelector(".wpcf7-response-output");

    setTimeout(() => {
      outPut.innerHTML = `<div class="first-line">Thank you.</div>
Your message has been received. `;
    }, 150);

    setTimeout(() => {
      gsap.fromTo(
        formPopUp,
        {
          opacity: 1,
        },
        {
          opacity: 0,
          onComplete: () => {
            formPopUp.classList.remove("active");
            form.classList.remove("sent");
          },
        },
      );
    }, 3000);
  }
});
